import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { OverlayRef, OverlayService } from "@lantern/app/core/utils/overlays";
import { AnchorPositionStrategy } from "@lantern/app/core/utils/overlays/position-strategies";

import {
  BaseOptionsDirective,
  BaseSelectComponent,
  SELECT_CLASSES,
  SelectClasses,
  getSelectClasses
} from "../base";
import { AdHocOptionsDirective } from "../shared/ad-hoc-options.directive";
import {
  MultiSelectDropdownComponent,
  MultiSelectWithDropdownComponent
} from "../shared/multi-select-dropdown.component";

import { MultiSelectPlaceholderLayoutDirective } from "./placeholder-layout.directive";

@Component({
  selector: "ta-multi-select",
  templateUrl: "./multi-select.template.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true
    },
    {
      provide: SELECT_CLASSES,
      useValue: getSelectClasses("select")
    }
  ]
})
export class MultiSelectComponent<
    Model,
    Identifier extends number | string,
    FieldValue extends Model | Identifier
  >
  extends BaseSelectComponent<Model, Identifier, FieldValue>
  implements
    MultiSelectWithDropdownComponent<Model, Identifier, FieldValue>,
    OnDestroy,
    OnInit,
    ControlValueAccessor
{
  @ContentChild(MultiSelectPlaceholderLayoutDirective, { static: true })
  placeholderLayout: TemplateRef<void>;

  @ViewChild("dropdownAnchor", { static: true }) dropdownAnchor: ElementRef;
  @ViewChildren(".select__selected-chip") selections: ElementRef[];

  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (this.selectedModels.length === 0) {
      return;
    }

    switch (event.key) {
      case "ArrowRight":
        this.handleArrowRight(event);
        break;
      case "ArrowLeft":
        this.handleArrowLeft(event);
        break;
      case "ArrowDown":
        this.handleArrowDown(event);
        break;
      case "Enter":
      case "Backspace":
        this.handleEnter(event);
        break;
      default:
        this.handleLetterKey(event);
    }
  }
  highlightedSelectionIndex: number = -1;

  private handleArrowRight(event: KeyboardEvent): void {
    if (this.highlightedSelectionIndex < this.selectedModels.length - 1) {
      this.highlightedSelectionIndex += 1;
    }
  }

  private handleArrowLeft(event: KeyboardEvent): void {
    if (this.highlightedSelectionIndex > 0) {
      this.highlightedSelectionIndex -= 1;
    }
  }

  private handleArrowDown(event: KeyboardEvent): void {
    this.highlightedSelectionIndex = -1;
    this.openDropdown();
    this.dropdownOverlayRef!.nativeElement.focus();
  }

  private handleEnter(event: KeyboardEvent): void {
    if (this.highlightedSelectionIndex < 0) {
      // nothing selected
      return;
    }

    this.removeSelectedModels(
      this.selectedModels[this.highlightedSelectionIndex]
    );

    if (this.highlightedSelectionIndex >= this.selectedModels.length) {
      this.highlightedSelectionIndex -= 1;
    }
  }

  private handleLetterKey(event: KeyboardEvent): void {
    if (event.key.length === 1 && event.key.match(/[a-z]/i)) {
      this.openDropdown();
      this.dropdownOverlayRef!.nativeElement.focus();
      this.highlightedSelectionIndex -= 1;
    }
  }

  @HostBinding("attr.tabindex")
  get tabIndex(): number {
    return this.disabled ? -1 : 0;
  }

  @Input()
  placeholder: string;

  @Input()
  valid: boolean = false;

  @Input()
  showValidation: boolean = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    value = coerceBooleanProperty(value);

    if (this._disabled === value) {
      return;
    }

    this._disabled = value;
    if (this._disabled) {
      this.open = false; // make sure dropdown is closed
      this.addHostClass(this.selectClasses.parentDisabled);
    } else {
      this.removeHostClass(this.selectClasses.parentDisabled);
    }
  }
  private _disabled: boolean;

  get open(): boolean {
    return this._open;
  }
  set open(value: boolean) {
    // always false when disabled or loading models
    if (this.disabled || this.loadingSelected) {
      value = false;
    } else {
      value = coerceBooleanProperty(value);
    }

    if (this.open === value) {
      return;
    }

    this._open = value;
    if (this.open) {
      this.getOptions();
      this.openDropdown();
      this.addHostClass(this.selectClasses.parentActive);
    } else {
      (this.elementRef.nativeElement as HTMLElement).focus();
      this.closeDropdown();
      this.removeHostClass(this.selectClasses.parentActive);
    }
  }
  private _open: boolean = false;

  @Input()
  get searchable(): boolean {
    return this._searchable;
  }
  set searchable(value: boolean) {
    this._searchable = coerceBooleanProperty(value);
  }
  private _searchable: boolean = false;

  @Input()
  get clearable(): boolean {
    return this._clearable;
  }
  set clearable(value: boolean) {
    this._clearable = coerceBooleanProperty(value);
  }
  private _clearable: boolean = false;

  @Input()
  get value(): FieldValue[] {
    return this._value;
  }
  set value(input: FieldValue[]) {
    input = input ?? [];
    if (this.value === input) {
      this.initializedModel = true;
      return;
    }
    this._value = input;
    this.addSelectedModels(...this._value);
  }
  private _value: FieldValue[] = [];

  @Output()
  valueChange: EventEmitter<FieldValue[]> = new EventEmitter<FieldValue[]>();

  @Input()
  get selectedModels(): Model[] {
    return this._selectedModels;
  }
  set selectedModels(models: Model[]) {
    // filter out duplicate models
    models = Array.from(
      new Set(models.map((model) => this.getIdentifierFromModel(model)))
    ).map(
      (identifier) =>
        models.find(
          (model) => identifier === this.getIdentifierFromModel(model)
        )!
    );

    const identifiers: Identifier[] = models.map((model) =>
      this.getIdentifierFromModel(model)
    );

    let value: FieldValue[];
    if (models.length === 0) {
      value = [];
    } else if (this.valueBy === "model") {
      value = models as FieldValue[];
    } else {
      value = identifiers as FieldValue[];
    }

    if (
      // if net change
      this.selectedIdentifiers.length !== identifiers.length ||
      !identifiers.every((identifier) =>
        this.selectedIdentifiers.includes(identifier)
      )
    ) {
      this.selectedIdentifiers = identifiers;

      this.value = value;
      this._selectedModels = models;

      this.valueChange.emit(value);
      this.selectedModelsChange.emit(models);
    }

    if (!this.initializedModel) {
      this.initializedModel = true;
    }
  }
  private _selectedModels: Model[] = [];

  @Output()
  selectedModelsChange: EventEmitter<Model[]> = new EventEmitter<Model[]>();

  private selectedIdentifiers: Identifier[] = [];

  constructor(
    injector: Injector,
    optionsSourceDirective: BaseOptionsDirective<Model, Identifier>,
    @Inject(SELECT_CLASSES) selectClasses: SelectClasses,
    public elementRef: ElementRef,
    private overlayService: OverlayService,
    renderer: Renderer2
  ) {
    super(
      injector,
      optionsSourceDirective,
      selectClasses,
      elementRef,
      renderer
    );
    this.initControlAccessors();

    this._searchable = !(
      optionsSourceDirective instanceof AdHocOptionsDirective
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setClasses();

    this.loadedOptions
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => this.addSelectedModels(...this.value));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.closeDropdown();
  }

  setSelected(models?: Model | Model[]): void {
    if (models == null) {
      this.selectedModels = [];
    } else if (Array.isArray(models)) {
      this.selectedModels = [...this.selectedModels, ...models];
    } else {
      this.selectedModels = [...this.selectedModels, models];
    }
  }

  isSelected(model: Model): boolean {
    return this.selectedIdentifiers.includes(
      this.getIdentifierFromModel(model)
    );
  }

  clearValue(event?: MouseEvent): void {
    if (event != null) {
      event.stopPropagation();
    }
    this.selectedModels = [];
  }

  get loadingSelected(): boolean {
    return this._loadingSelected;
  }
  private _loadingSelected: boolean = false;

  private addSelectedModelSubscription?: Subscription;
  addSelectedModels(...values: FieldValue[]): void {
    if (
      this.addSelectedModelSubscription != null &&
      !this.addSelectedModelSubscription.closed
    ) {
      this.addSelectedModelSubscription.unsubscribe();
    }

    if (values.length === 0) {
      this._loadingSelected = false;
      this.addSelectedModelSubscription = undefined;
      this.selectedModels = [];
      return;
    }

    function valueIsIdentifier(value: FieldValue): boolean {
      return typeof value === "string" || typeof value === "number";
    }

    const fieldValues =
      this.valueBy === "id"
        ? values.map((value) =>
            valueIsIdentifier(value)
              ? (value as Identifier)
              : this.getIdentifierFromModel(value as Model)
          )
        : values;

    this._loadingSelected = true;
    this.addSelectedModelSubscription = this.getModelsFromFieldValues(
      fieldValues as FieldValue[]
    ).subscribe(
      (newModels) => {
        this._loadingSelected = false;
        this.selectedModels = [...this.selectedModels, ...newModels];
      },
      () => {
        // TODO: error, failure to lookup values
        this._loadingSelected = false;
        throw new Error();
      }
    );
  }

  removeSelectedModels(...models: Model[]): void {
    const identifiersToRemove = new Set(
      models.map((model) => this.getIdentifierFromModel(model))
    );

    this.selectedModels = this.selectedModels.filter(
      (model) => !identifiersToRemove.has(this.getIdentifierFromModel(model))
    );
  }

  private dropdownOverlayRef?: OverlayRef;
  private dropdownPositionStrategy?: AnchorPositionStrategy;
  private openDropdown(): void {
    if (this.dropdownOverlayRef != null) {
      return;
    }

    if (this.dropdownPositionStrategy == null) {
      this.dropdownPositionStrategy = new AnchorPositionStrategy(
        this.dropdownAnchor.nativeElement,
        ["bottom-left", "bottom-right", "top-left", "top-right"],
        4
      );
    }
    this.dropdownOverlayRef = this.overlayService.createComponent(
      MultiSelectDropdownComponent,
      this.dropdownPositionStrategy,
      this.injector
    );
  }

  private closeDropdown(): void {
    if (this.dropdownOverlayRef == null) {
      return;
    }
    this.dropdownOverlayRef.destroy();
    this.dropdownOverlayRef = undefined;
  }

  private setClasses(): void {
    this.renderer.addClass(
      this.dropdownAnchor.nativeElement,
      this.selectClasses.selection
    );
  }

  @HostListener("click", ["$event"])
  onClickInteraction(event: Event): void {
    event.preventDefault();
    this.touched = true;
    this.open = !this.open;
  }

  @HostListener("keydown.space", ["$event"])
  onSpacePress(event: Event): void {
    event.preventDefault();
    this.touched = true;
    this.open = !this.open;
  }

  @HostListener("window:resize", ["$event"])
  onWindowResize(): void {
    this.open = false;
  }

  initControlAccessors(): void {
    super.initControlAccessors();

    this.valueChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((value) => {
        if (this._onChangeFn == null || !this.initializedModel) {
          return;
        }
        this._onChangeFn(value);
      });
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
